import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import AppHeader from "@/components/app/AppHeader";
import AppSection from "@/components/app/AppSection";
import { StatusBadge } from "@/components/app/StatusBadge";
import CTAButton from "@/components/app/CTAButton";
import AppModal from "@/components/app/AppModal";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { api, formatApiError } from "@/lib/api";
import { LogOut, Sun, Moon, UserPlus } from "lucide-react";

export default function AppProfile() {
    const { user, refreshUser, logout } = useAuth();
    const { mode, toggle } = useTheme();
    const navigate = useNavigate();
    const [confirm, setConfirm] = useState(false);
    const [error, setError] = useState("");

    const roles = user?.roles || [];
    const canAddSeller = !roles.includes("seller");

    async function addSellerRole() {
        setError("");
        try {
            await api.post("/auth/add-role", { role: "seller" });
            await refreshUser();
        } catch (e) {
            setError(formatApiError(e));
        }
    }

    return (
        <>
            <AppHeader title="Mon profil" />
            <AppSection>
                <div className="rounded-2xl border border-md-border bg-md-surface p-6">
                    <p className="md-overline">Compte</p>
                    <h2 className="mt-2 text-xl font-bold tracking-tight text-md-text">{user?.pseudo}</h2>
                    <p className="mt-1 text-sm text-md-text-2">{user?.email}</p>
                    <div className="mt-3 flex flex-wrap gap-2">
                        {roles.includes("buyer") && <StatusBadge tone="primary">Chercheur</StatusBadge>}
                        {roles.includes("seller") && <StatusBadge tone="gold">Dresseur</StatusBadge>}
                        {user?.seller_status && (
                            <StatusBadge tone={user.seller_status === "certified" ? "success" : "warning"}>
                                Onboarding : {user.seller_status}
                            </StatusBadge>
                        )}
                    </div>
                </div>
            </AppSection>

            <AppSection title="Préférences">
                <button
                    onClick={toggle}
                    data-testid="profile-theme-toggle"
                    className="flex w-full items-center justify-between rounded-2xl border border-md-border bg-md-surface px-5 py-4 text-left hover:bg-md-surface-2"
                >
                    <div>
                        <p className="font-medium text-md-text">Thème {mode === "dark" ? "sombre" : "clair"}</p>
                        <p className="text-xs text-md-text-2">Bascule l’interface en un toucher.</p>
                    </div>
                    {mode === "dark" ? <Sun className="h-5 w-5 text-md-text-2" /> : <Moon className="h-5 w-5 text-md-text-2" />}
                </button>

                {canAddSeller && (
                    <button
                        onClick={addSellerRole}
                        data-testid="profile-add-seller"
                        className="mt-3 flex w-full items-center justify-between rounded-2xl border border-md-border bg-md-surface px-5 py-4 text-left hover:bg-md-surface-2"
                    >
                        <div>
                            <p className="font-medium text-md-text">Devenir aussi dresseur</p>
                            <p className="text-xs text-md-text-2">Ajoute le rôle vendeur à ton compte.</p>
                        </div>
                        <UserPlus className="h-5 w-5 text-md-text-2" />
                    </button>
                )}
                {error && <p className="mt-2 text-xs text-md-danger">{error}</p>}
            </AppSection>

            <AppSection>
                <CTAButton
                    variant="outline"
                    size="block"
                    icon={<LogOut className="h-4 w-4" />}
                    onClick={() => setConfirm(true)}
                    testId="profile-logout"
                >
                    Se déconnecter
                </CTAButton>
            </AppSection>

            <AppModal
                open={confirm}
                onOpenChange={setConfirm}
                title="Quitter l’aventure ?"
                subtitle="Tu pourras te reconnecter à tout moment."
                variant="confirmation"
                primaryAction={{
                    label: "Me déconnecter",
                    onClick: async () => { await logout(); navigate("/login", { replace: true }); },
                }}
                secondaryAction={{ label: "Rester", onClick: () => setConfirm(false) }}
            >
                <p className="text-sm text-md-text-2">
                    Ton historique reste disponible à ta prochaine connexion.
                </p>
            </AppModal>
        </>
    );
}
